import { useCallback, useMemo, useRef, useState } from 'react';
import PeriodPicker from './PeriodPicker';
import { PRESETS_DOCUMENTS } from '../utils/period';
import MultiSelectFilter from './MultiSelectFilter';
import FilterButton from './FilterButton';
import FilterSheet from './FilterSheet';
import { useIsMobile } from '../utils/useIsMobile';
import { useStickyFilterOffset } from '../utils/useStickyFilterOffset';
import Icon from './Icon';
import { docTypeFilterLabel } from '../utils/docTypes';
import { PRICE_SOURCE_OPTIONS, PRICE_SOURCE_FILTER_LABEL, priceSourceLabel, PriceSourceInfo } from './PriceSourceBadge';

const PRICE_SOURCE_KEYS = PRICE_SOURCE_OPTIONS.map((o) => o.key);
const renderPriceSourceInfo = (key) => <PriceSourceInfo optionKey={key} />;

// Filter bar scoped to /documents. Follows FILTER_AND_DATE_RULES.md:
// categorical filters cluster on the start side as multi-select pills,
// date picker anchored to the far end via its own .docs-filter-datewrap
// (margin-inline-start: auto), active-filter chips render below (date
// excluded), and "Clear all" sits last in the chip row.
//
// Backend supports comma-separated lists for supplier_id and doc_type —
// see buildAllDocsFilter in server/routes/documents.js.
//
// ≤720px the bar collapses to the doc-number search + a "סינון" trigger;
// supplier/doc-type/date filters move into a FilterSheet (bottom sheet).
export default function DocumentsFilterBar({
  suppliers,
  supplierIds,
  setSupplierIds,
  docTypes,
  setDocTypes,
  docTypeOptions,
  docNumFilter,
  setDocNumFilter,
  dateFrom,
  dateTo,
  setDateRange,
  onClearAll,
  resultCount,
  // Optional mobile sort control (MobileSort node) — rendered beside the
  // סינון trigger; pages that support sorting pass it in.
  sortControl,
  // Admin-only "מחירים אוטומטיים" filter. Rendered only when setPriceSources is
  // passed — the page passes it for admins only.
  priceSources = [],
  setPriceSources,
}) {
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(false);
  const showPriceSource = typeof setPriceSources === 'function';
  // Not applied when the filter isn't offered (e.g. an admin previewing as a
  // manager), so it doesn't show as active either.
  const priceSourceActive = showPriceSource && priceSources.length > 0;
  const hasChips = supplierIds.length > 0 || docTypes.length > 0 || Boolean(docNumFilter) || priceSourceActive;
  const sheetActiveCount =
    (supplierIds.length > 0 ? 1 : 0) + (docTypes.length > 0 ? 1 : 0) + (priceSourceActive ? 1 : 0);

  // Memoized on `suppliers` reference. Without this the option list and
  // id→name map are fresh arrays/maps on every render, which invalidates
  // child memos in MultiSelectFilter and re-runs the searchable filter.
  const supplierIdToName = useMemo(
    () => new Map(suppliers.map(s => [String(s.id), s.name])),
    [suppliers],
  );
  const supplierOptions = useMemo(
    () => suppliers.map(s => String(s.id)),
    [suppliers],
  );
  // Stable identity helps any consumer that memoizes against it.
  const renderSupplierOption = useCallback(
    (id) => supplierIdToName.get(id) || id,
    [supplierIdToName],
  );
  const supplierIdsAsStrings = useMemo(
    () => supplierIds.map(String),
    [supplierIds],
  );
  const supplierNames = (ids) => ids.map(id => supplierIdToName.get(String(id)) || id);
  const wrapRef = useRef(null);
  useStickyFilterOffset(wrapRef);

  const docNumPill = (
    <label className={`docs-filter-control docs-filter-searchpill${docNumFilter ? ' is-active' : ''}`}>
      <span className="docs-filter-microlabel" aria-label="חיפוש לפי מספר מסמך">#</span>
      <input
        className="docs-filter-select"
        value={docNumFilter}
        onChange={(e) => setDocNumFilter(e.target.value)}
        placeholder="מספר מסמך"
        style={{ minWidth: isMobile ? 0 : 120 }}
      />
    </label>
  );

  const supplierSelect = (inline) => (
    <MultiSelectFilter
      inline={inline}
      label="ספק"
      values={supplierIdsAsStrings}
      options={supplierOptions}
      onChange={setSupplierIds}
      renderOption={renderSupplierOption}
      searchable
      minSearchChars={2}
    />
  );
  const docTypeSelect = (inline) => (
    <MultiSelectFilter
      inline={inline}
      label="סוג מסמך"
      values={docTypes}
      options={docTypeOptions}
      onChange={setDocTypes}
      renderOption={docTypeFilterLabel}
      searchable
      minSearchChars={2}
    />
  );
  const priceSourceSelect = (inline) => showPriceSource && (
    <MultiSelectFilter
      inline={inline}
      label={PRICE_SOURCE_FILTER_LABEL}
      values={priceSources}
      options={PRICE_SOURCE_KEYS}
      onChange={setPriceSources}
      renderOption={priceSourceLabel}
      renderOptionInfo={renderPriceSourceInfo}
    />
  );
  // One date control for both months and arbitrary day ranges. `compact` is the
  // mobile-row variant — same component and state, just no microlabel.
  const dateRange = (variant) => (
    <PeriodPicker
      dateFrom={dateFrom}
      dateTo={dateTo}
      onChange={(f, t) => setDateRange(f, t)}
      presets={PRESETS_DOCUMENTS}
      variant={variant}
    />
  );

  const renderChips = () => (
    <div className="docs-filter-chips">
      {supplierIds.length > 0 && (
        <span className="docs-filter-chip">
          ספק: {supplierNames(supplierIds).slice(0, 3).join(', ')}
          {supplierIds.length > 3 && ` +${supplierIds.length - 3}`}
          <button
            type="button"
            aria-label="נקה ספק"
            onClick={() => setSupplierIds([])}
            className="docs-filter-chip-x"
          ><Icon name="close" size={11} strokeWidth={2} /></button>
        </span>
      )}
      {docTypes.length > 0 && (
        <span className="docs-filter-chip">
          סוג מסמך: {docTypes.slice(0, 3).map(docTypeFilterLabel).join(', ')}
          {docTypes.length > 3 && ` +${docTypes.length - 3}`}
          <button
            type="button"
            aria-label="נקה סוג מסמך"
            onClick={() => setDocTypes([])}
            className="docs-filter-chip-x"
          ><Icon name="close" size={11} strokeWidth={2} /></button>
        </span>
      )}
      {priceSourceActive && (
        <span className="docs-filter-chip">
          {PRICE_SOURCE_FILTER_LABEL}: {priceSources.map(priceSourceLabel).join(', ')}
          <button
            type="button"
            className="docs-filter-chip-x"
            aria-label={`נקה ${PRICE_SOURCE_FILTER_LABEL}`}
            onClick={() => setPriceSources([])}
          ><Icon name="close" size={11} strokeWidth={2} /></button>
        </span>
      )}
      {docNumFilter && (
        <span className="docs-filter-chip">
          מספר מסמך: {docNumFilter}
          <button
            type="button"
            aria-label="נקה מספר מסמך"
            onClick={() => setDocNumFilter('')}
            className="docs-filter-chip-x"
          ><Icon name="close" size={11} strokeWidth={2} /></button>
        </span>
      )}
      <button type="button" className="docs-filter-clearall" onClick={onClearAll}>
        נקה הכל
      </button>
    </div>
  );

  if (isMobile) {
    return (
      <>
      <div className="period-bar" ref={wrapRef}>{dateRange('bar')}</div>
      <div className="docs-filter-wrap">
        <div className="docs-filter-bar docs-filter-bar--mobile">
          {docNumPill}
          <FilterButton
            activeCount={sheetActiveCount}
            expanded={sheetOpen}
            onClick={() => setSheetOpen(true)}
          />
          {sortControl}
        </div>
        {hasChips && renderChips()}

        <FilterSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          resultCount={resultCount}
          onClearAll={onClearAll}
        >
          {supplierSelect(true)}
          {docTypeSelect(true)}
          {priceSourceSelect(true)}
        </FilterSheet>
      </div>
      </>
    );
  }

  return (
    <div className="docs-filter-wrap" ref={wrapRef}>
      <div className="docs-filter-bar">
        {supplierSelect(false)}
        {docTypeSelect(false)}
        {priceSourceSelect(false)}
        {docNumPill}
        {dateRange('pill')}
      </div>

      {hasChips && renderChips()}
    </div>
  );
}
